import React, { useState, useEffect } from 'react';
import { FiX, FiUser, FiPhone, FiDollarSign, FiCalendar, FiGlobe, FiRefreshCw, FiMapPin } from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { FREQUENCY_LABELS } from '../../services/billing';

const initialForm = {
  client_name: '',
  phone_number: '',
  monthly_amount: '',
  currency: 'SGD',
  billing_frequency: 'monthly',
  website_url: '',
  next_due_date: '',
  country: 'SG'
};

export default function ClientForm({ onSave, saving, onClose, editClient, countries }) {
  const [form, setForm] = useState(initialForm);

  // Build country → currency mapping
  const countryCurrencyMap = {};
  (countries || []).forEach(c => {
    countryCurrencyMap[c.code] = c.currency_code;
  });

  useEffect(() => {
    if (editClient) {
      setForm({
        client_name: editClient.client_name || '',
        phone_number: editClient.phone_number || '',
        monthly_amount: String(editClient.monthly_amount || ''),
        currency: editClient.currency || 'SGD',
        billing_frequency: editClient.billing_frequency || 'monthly',
        website_url: editClient.website_url || '',
        next_due_date: editClient.next_due_date || '',
        country: editClient.country || 'SG'
      });
    } else {
      setForm(initialForm);
    }
  }, [editClient]);

  const setField = (field, value) => {
    setForm(current => {
      const next = { ...current, [field]: value };
      // Auto-set currency when country changes
      if (field === 'country' && countryCurrencyMap[value]) {
        next.currency = countryCurrencyMap[value];
      }
      return next;
    });
  };

  const submit = async (event) => {
    event.preventDefault();
    const name = form.client_name.trim();
    const phone = form.phone_number.replace(/[\s()-]/g, '');
    if (!name || !/^\+[1-9]\d{6,14}$/.test(phone)) {
      window.alert('Enter a client name and a phone number in international format, such as +6589180134.');
      return;
    }
    const saved = await onSave({
      client_name: name,
      phone_number: phone,
      monthly_amount: Number(form.monthly_amount),
      currency: form.currency,
      billing_frequency: form.billing_frequency,
      website_url: form.website_url.trim(),
      next_due_date: form.next_due_date,
      country: form.country
    });
    if (saved && !editClient) setForm(initialForm);
  };

  const inputClass = 'w-full rounded-xl border border-slate-200 bg-white px-4 py-3.5 pl-11 text-slate-900 outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100';
  const isEditing = !!editClient;

  return (
    <form onSubmit={submit} className="rounded-3xl border border-slate-200 bg-white p-6 shadow-2xl md:p-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-slate-900">
            {isEditing ? 'Edit Client' : 'Add New Client'}
          </h2>
          <p className="text-sm text-slate-500">
            {isEditing ? 'Update client details' : 'Set up a new billing record'}
          </p>
        </div>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 text-slate-400 transition hover:bg-slate-50 hover:text-slate-600"
          >
            <SafeIcon icon={FiX} className="w-5 h-5" />
          </button>
        )}
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-2">Client Name</label>
          <div className="relative">
            <SafeIcon icon={FiUser} className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              className={inputClass}
              required
              maxLength={160}
              placeholder="John Doe"
              value={form.client_name}
              onChange={(e) => setField('client_name', e.target.value)}
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-2">WhatsApp Phone</label>
          <div className="relative">
            <SafeIcon icon={FiPhone} className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              className={inputClass}
              type="tel"
              required
              placeholder="+6589180134"
              value={form.phone_number}
              onChange={(e) => setField('phone_number', e.target.value)}
            />
          </div>
          <p className="mt-1.5 text-xs text-slate-400">Include country code (e.g., +65 for Singapore)</p>
        </div>

        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-2">Country</label>
          <div className="relative">
            <SafeIcon icon={FiMapPin} className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <select
              className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3.5 pl-11 text-slate-900 outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100 appearance-none"
              value={form.country}
              onChange={(e) => setField('country', e.target.value)}
            >
              {(countries || []).filter(c => c.is_active).map(c => (
                <option key={c.code} value={c.code}>
                  {c.flag} {c.name} ({c.currency_code})
                </option>
              ))}
            </select>
          </div>
          <p className="mt-1.5 text-xs text-slate-400">Currency auto-fills based on country</p>
        </div>

        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-2">
            Website URL <span className="text-slate-400 font-normal">(optional)</span>
          </label>
          <div className="relative">
            <SafeIcon icon={FiGlobe} className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              className={inputClass}
              type="url"
              placeholder="https://example.com"
              value={form.website_url}
              onChange={(e) => setField('website_url', e.target.value)}
            />
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">Amount per Cycle</label>
            <div className="relative">
              <SafeIcon icon={FiDollarSign} className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                className={inputClass}
                type="number"
                min="0.01"
                step="0.01"
                required
                placeholder="500.00"
                value={form.monthly_amount}
                onChange={(e) => setField('monthly_amount', e.target.value)}
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">Currency</label>
            <select
              className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3.5 text-slate-900 outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
              value={form.currency}
              onChange={(e) => setField('currency', e.target.value)}
            >
              <option value="SGD">SGD - Singapore Dollar</option>
              <option value="LKR">LKR - Sri Lankan Rupee</option>
              <option value="USD">USD - US Dollar</option>
              <option value="EUR">EUR - Euro</option>
              <option value="GBP">GBP - British Pound</option>
              <option value="AUD">AUD - Australian Dollar</option>
              <option value="CAD">CAD - Canadian Dollar</option>
              <option value="JPY">JPY - Japanese Yen</option>
              <option value="INR">INR - Indian Rupee</option>
              <option value="MYR">MYR - Malaysian Ringgit</option>
              <option value="THB">THB - Thai Baht</option>
              <option value="IDR">IDR - Indonesian Rupiah</option>
              <option value="PHP">PHP - Philippine Peso</option>
              <option value="KRW">KRW - South Korean Won</option>
              <option value="CNY">CNY - Chinese Yuan</option>
              <option value="HKD">HKD - Hong Kong Dollar</option>
              <option value="TWD">TWD - Taiwan Dollar</option>
              <option value="NZD">NZD - New Zealand Dollar</option>
              <option value="CHF">CHF - Swiss Franc</option>
              <option value="AED">AED - UAE Dirham</option>
              <option value="SAR">SAR - Saudi Riyal</option>
              <option value="ZAR">ZAR - South African Rand</option>
              <option value="BRL">BRL - Brazilian Real</option>
              <option value="MXN">MXN - Mexican Peso</option>
              <option value="PKR">PKR - Pakistani Rupee</option>
              <option value="BDT">BDT - Bangladeshi Taka</option>
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-2">Billing Frequency</label>
          <div className="relative">
            <SafeIcon icon={FiRefreshCw} className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <select
              className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3.5 pl-11 text-slate-900 outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
              value={form.billing_frequency}
              onChange={(e) => setField('billing_frequency', e.target.value)}
            >
              {Object.entries(FREQUENCY_LABELS).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>
          <p className="mt-1.5 text-xs text-slate-400">
            Reminder emails are sent on each due date based on this frequency
          </p>
        </div>

        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-2">Next Due Date</label>
          <div className="relative">
            <SafeIcon icon={FiCalendar} className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              className={inputClass}
              type="date"
              required
              value={form.next_due_date}
              onChange={(e) => setField('next_due_date', e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="mt-8 flex gap-3">
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="flex-1 rounded-xl border border-slate-200 bg-white px-6 py-3.5 font-semibold text-slate-700 transition hover:bg-slate-50"
          >
            Cancel
          </button>
        )}
        <button
          type="submit"
          disabled={saving}
          className="flex-1 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-3.5 font-semibold text-white transition hover:from-blue-700 hover:to-indigo-700 disabled:opacity-50 shadow-lg shadow-blue-600/25"
        >
          {saving ? (
            <span className="flex items-center justify-center gap-2">
              <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Saving...
            </span>
          ) : (
            isEditing ? 'Save Changes' : 'Add Client'
          )}
        </button>
      </div>
    </form>
  );
}